import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása mindhárom törzs-alakból / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)"),
// a nyers `err.response?.data?.message` helyett. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../../utils/apiErrors';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  Fab,
} from '@mui/material';
import { DayEvent } from '../../types/calendar';
import { calendarService } from '../../services/calendarService';
import EventDetailsDialog from './EventDetailsDialog';
import { RegisteredIcon, registeredAriaLabel, showRegisteredMarker } from './RegisteredMarker';
import EventCreateDialog from './EventCreateDialog';
import EventEditDialog from './EventEditDialog';
import { formatDateToLocal } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { NAV_BUTTON_TOUCH_SX, chipFocusRingSx } from './calendarA11y';

interface MonthlyCalendarProps {
  onDateSelect?: (date: string) => void;
  selectedDate?: string;
  venueFilter?: string;
  availableOnly?: boolean;
  /** Elrejti a betelt eseményeket, amelyeknek a felhasználó nem résztvevője. */
  excludeFullOthers?: boolean;
  eventTypeFilter?: string[];
  coachFilter?: string[];
  refreshTrigger?: number;
  onNavigateToUserProfile?: (userId: string) => void;
  /** Az esemény ablakából a jelenléti ívre visz, ARRA az eseményre. */
  onNavigateToAttendance?: (eventId: string, eventDate: string) => void;
}

const MonthlyCalendar: React.FC<MonthlyCalendarProps> = ({
  onDateSelect,
  selectedDate,
  venueFilter,
  availableOnly,
  excludeFullOthers,
  eventTypeFilter,
  coachFilter,
  refreshTrigger,
  onNavigateToUserProfile,
  onNavigateToAttendance
}) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthlyEvents, setMonthlyEvents] = useState<DayEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Dialog states
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [eventCreateOpen, setEventCreateOpen] = useState(false);
  const [eventEditOpen, setEventEditOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [createEventDate, setCreateEventDate] = useState<string>('');

  const hungaryDayNames = ['Hét', 'Ked', 'Sze', 'Csü', 'Pén', 'Szo', 'Vas']; // Monday to Sunday
  const hungaryMonthNames = [
    'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
    'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'
  ];

  useEffect(() => {
    loadCalendarData();
  }, [currentDate, venueFilter, availableOnly, excludeFullOthers, eventTypeFilter, coachFilter, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      setError('');

      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      console.log('MonthlyCalendar: Loading calendar data for', year, month, 'with filters:', {
        venueFilter,
        availableOnly,
        excludeFullOthers,
        eventTypeFilter,
        coachFilter
      });
      const monthlyData = await calendarService.getMonthlySummary(
        year,
        month,
        venueFilter,
        availableOnly,
        eventTypeFilter,
        coachFilter,
        excludeFullOthers
      );
      console.log('MonthlyCalendar: Loaded monthly data:', monthlyData);
      setMonthlyEvents(monthlyData);
    } catch (err: any) {
      console.error('MonthlyCalendar: Error loading calendar data:', err);
      setError(apiErrorMessage(err, 'Hiba történt a naptár adatok betöltésekor'));
    } finally {
      setLoading(false);
    }
  };


  const navigateMonth = (direction: 'prev' | 'next') => {
    console.log('🔵 navigateMonth clicked:', direction, 'current loading:', loading);
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      // Set to first day of month to avoid date overflow issues
      // (e.g., Jan 31 + 1 month = Feb 31 -> Mar 3)
      newDate.setDate(1);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      console.log('🟢 navigateMonth changing from:', prevDate.toISOString().split('T')[0], 'to:', newDate.toISOString().split('T')[0]);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayWeekday = (firstDay.getDay() + 6) % 7; // Adjust for Monday start (0=Monday, 6=Sunday)

    const days: Date[] = [];

    // Add days from previous month to fill the first week
    const prevMonth = new Date(year, month, 0); // Last day of previous month (day 0 = last day of month-1)
    const prevMonthDays = prevMonth.getDate();
    for (let i = firstDayWeekday - 1; i >= 0; i--) {
      days.push(new Date(year, month - 1, prevMonthDays - i));
    }

    // Add all days of the current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }

    // Fill remaining cells with next month's days to complete 6 weeks = 42 cells
    let nextMonthDay = 1;
    const nextYear = month === 11 ? year + 1 : year;
    const nextMonthIndex = month === 11 ? 0 : month + 1;

    while (days.length < 42) {
      days.push(new Date(nextYear, nextMonthIndex, nextMonthDay));
      nextMonthDay++;
    }

    return days;
  };

  const getEventsForDate = (date: Date): DayEvent | undefined => {
    const dateStr = formatDateToLocal(date);
    return monthlyEvents.find(event => event.date === dateStr);
  };

  const handleDateClick = (date: Date) => {
    const dateStr = formatDateToLocal(date);
    onDateSelect?.(dateStr);
  };

  const handleEventClick = (event: React.MouseEvent, eventId: string) => {
    event.stopPropagation();
    setSelectedEventId(eventId);
    setEventDetailsOpen(true);
  };

  const handleCreateEventClick = (date?: Date) => {
    if (date) {
      const dateStr = formatDateToLocal(date);
      setCreateEventDate(dateStr);
    } else {
      setCreateEventDate('');
    }
    setEventCreateOpen(true);
  };

  const handleEditEvent = (eventId: string) => {
    setSelectedEventId(eventId);
    setEventDetailsOpen(false);
    setEventEditOpen(true);
  };

  const handleDeleteEvent = (eventId: string) => {
    setEventDetailsOpen(false);
    loadCalendarData(); // Reload calendar data
  };

  const handleEventUpdated = () => {
    console.log('MonthlyCalendar: Event updated, reloading calendar data...');
    loadCalendarData(); // Reload calendar data
  };

  const isSelectedDate = (date: Date): boolean => {
    if (!selectedDate) return false;
    const dateStr = formatDateToLocal(date);
    return dateStr === selectedDate;
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === currentDate.getMonth() && date.getFullYear() === currentDate.getFullYear();
  };

  // Check if user can create events (not Player role)
  const canCreateEvents = user && user.userRole !== 'Player';

  const days = getDaysInMonth();

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Calendar Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          mb: 2,
          flexWrap: 'wrap',
          gap: 1
        }}>
          {/* [Web UX 9 / T5, 2026-10-13] címke nélküli lapozók (`MonthlyCalendar.tsx:259,273`) → aria-label +
              telefonon 44 px. Őr: `utils/__tests__/iconButtonLabels.guard.test.ts`. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            <IconButton onClick={() => navigateMonth('prev')} size="small" disabled={loading} aria-label="Előző hónap" sx={NAV_BUTTON_TOUCH_SX}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                minWidth: { xs: 150, sm: 200 },
                textAlign: 'center',
                fontSize: { xs: '1.1rem', sm: '1.5rem' },
                fontWeight: 'medium'
              }}
            >
              {hungaryMonthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </Typography>
            <IconButton onClick={() => navigateMonth('next')} size="small" disabled={loading} aria-label="Következő hónap" sx={NAV_BUTTON_TOUCH_SX}>
              <ChevronRightIcon />
            </IconButton>
            <Tooltip title="Mai nap">
              <IconButton onClick={goToToday} size="small" disabled={loading} aria-label="Mai nap" sx={NAV_BUTTON_TOUCH_SX}>
                <TodayIcon />
              </IconButton>
            </Tooltip>
          </Box>

        </Box>
      </Paper>

      {/* Calendar Grid */}
      <Paper sx={{ p: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Day headers */}
            <Box sx={{ display: 'flex' }}>
              {hungaryDayNames.map((day) => (
                <Box
                  key={day}
                  sx={{
                    flex: '1 0 14.28%',
                    p: 1,
                    textAlign: 'center',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? theme.palette.grey[800]
                        : theme.palette.grey[50],
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Calendar days */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {days.map((date, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: '1 0 14.28%',
                    minHeight: { xs: 90, sm: 120 },
                    maxHeight: { xs: 90, sm: 120 },
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 0.5,
                    overflow: 'hidden',
                  }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      backgroundColor: isSelectedDate(date)
                        ? 'primary.50'
                        : isToday(date)
                        ? 'warning.50'
                        : !isCurrentMonth(date)
                        ? 'action.hover'
                        : 'transparent',
                      opacity: isCurrentMonth(date) ? 1 : 0.7,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                    onClick={() => handleDateClick(date)}
                    onDoubleClick={canCreateEvents ? () => handleCreateEventClick(date) : undefined}
                  >
                    <CardContent sx={{
                      p: 1,
                      '&:last-child': { pb: 1 },
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden'
                    }}>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 0.5,
                          fontWeight: isToday(date) ? 'bold' : 'normal',
                          color: isToday(date)
                            ? 'warning.main'
                            : isCurrentMonth(date)
                              ? 'text.primary'
                              : 'text.disabled',
                          opacity: isCurrentMonth(date) ? 1 : 0.6,
                        }}
                      >
                        {date.getDate()}
                      </Typography>

                      {(() => {
                        const dayEvents = getEventsForDate(date);
                        return dayEvents ? (
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                              {dayEvents.eventCount} esemény
                            </Typography>
                            <Box sx={{
                              mt: 0.5,
                              flex: 1,
                              overflow: 'auto',
                              maxHeight: '70px'
                            }}>
                              {dayEvents.events.slice(0, 3).map((event, eventIndex) => {
                                const chipLabel = event.eventType === 'Asztalfoglalás' ? event.eventName : `${event.eventName} (${event.maxParticipants ? `${event.participantCount}/${event.maxParticipants}` : event.participantCount})`;
                                // [Web UX 1. csomag, 2026-10-03] „Jelentkeztél": a 16 px-es chipbe
                                // szöveg nem fér, ezért pipa-ikon + akadálymentes név és súgó
                                // (title) — nem csak szín. A függő állapot előzi (közös segéd).
                                const registered = showRegisteredMarker(event);
                                return (
                                <Chip
                                  key={eventIndex}
                                  label={chipLabel}
                                  icon={registered ? <RegisteredIcon /> : undefined}
                                  aria-label={registered ? registeredAriaLabel(chipLabel) : undefined}
                                  title={registered ? registeredAriaLabel(chipLabel) : undefined}
                                  size="small"
                                  onClick={(e) => handleEventClick(e, event.eventId)}
                                  sx={{
                                    mb: 0.5,
                                    mr: 0.5,
                                    height: 16,
                                    fontSize: '0.6rem',
                                    backgroundColor: event.isPending ? '#f44336' : event.color,
                                    color: 'white',
                                    cursor: 'pointer',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                      px: 1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    },
                                    // a pipa a fehér felirattal egyező színű, a chip magasságához méretezve
                                    '& .MuiChip-icon': {
                                      color: 'white',
                                      fontSize: '0.75rem',
                                      ml: '4px',
                                      mr: '-6px',
                                      verticalAlign: 'middle',
                                    },
                                    '&:hover': {
                                      opacity: 0.8,
                                    },
                                    // [Web UX 9 / T5, 2026-10-13] A kattintható chip (MUI ButtonBase: role=button,
                                    // Enter/Szóköz) billentyűzetről már elérhető volt, de a fókuszát csak a MUI
                                    // `Mui-focusVisible` háttér-árnyalata jelezte, ami tetszőleges típusszínen nem
                                    // garantáltan látszik.
                                    // Kétszínű, BEFELÉ rajzolt gyűrű (a tároló `overflow: auto`-ja levágná a külsőt).
                                    ...chipFocusRingSx,
                                  }}
                                />
                                );
                              })}
                              {dayEvents.events.length > 3 && (
                                <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                                  +{dayEvents.events.length - 3} további
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ) : null;
                      })()}
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Floating Action Button - only show for users who can create events */}
      {canCreateEvents && (
        <Fab
          color="primary"
          aria-label="Új esemény létrehozása"
          onClick={() => handleCreateEventClick()}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 80 : 16, // Above mobile bottom navigation when on mobile
            right: 16,
            zIndex: 1000,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialogs */}
      <EventDetailsDialog
        open={eventDetailsOpen}
        eventId={selectedEventId}
        onClose={() => {
          setEventDetailsOpen(false);
          setSelectedEventId(null);
        }}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
        onEventUpdated={handleEventUpdated}
        onNavigateToUserProfile={onNavigateToUserProfile}
        onNavigateToAttendance={onNavigateToAttendance}
      />

      <EventCreateDialog
        open={eventCreateOpen}
        onClose={() => {
          setEventCreateOpen(false);
          setCreateEventDate('');
        }}
        onEventCreated={handleEventUpdated}
        initialDate={createEventDate}
      />

      <EventEditDialog
        open={eventEditOpen}
        eventId={selectedEventId}
        onClose={() => {
          setEventEditOpen(false);
          setSelectedEventId(null);
        }}
        onEventUpdated={handleEventUpdated}
      />
    </Box>
  );
};

export default MonthlyCalendar;